/**
 * /equipment/cost-posting — 3-step wizard to post unposted equipment time
 * entries to a single journal.
 *
 * Step 1  Filter + select unposted entries (cross-equipment)
 * Step 2  Posting date / period / notes
 * Step 3  Preview the DR/CR shape + Post
 *
 * The backend (POST /api/construction/equipment/cost-postings) is
 * idempotent + validates: all selected entries unposted, period open,
 * single clearing account across the batch (returns 422 otherwise).
 */
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import {
  equipmentItemsQueryOptions,
  equipmentPostingKeys,
  equipmentUnpostedTimeEntriesQueryOptions,
  periodsListQueryOptions,
} from "@/lib/queries";
import { ProjectIdSelect } from "@/components/ProjectIdSelect";
import type {
  EquipmentCostPosting,
  EquipmentItem,
  EquipmentTimeEntry,
} from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";
import { useToast } from "@/components/Toaster";
import { useConstructionPack } from "@/lib/useConstructionPack";

export const Route = createFileRoute("/_auth/equipment/cost-posting")({
  component: CostPostingWizard,
});

function fmtMoney(v: string | number | null | undefined) {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n as number);
}

const STEPS = [
  "Select unposted entries",
  "Posting details",
  "Preview & post",
];

function CostPostingWizard() {
  const { enabled, loading: packLoading } = useConstructionPack();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [stepIdx, setStepIdx] = useState(0);

  // Step 1 filters
  const [equipmentFilter, setEquipmentFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  // Step 2 inputs
  const today = new Date().toISOString().slice(0, 10);
  const [postingDate, setPostingDate] = useState(today);
  const [periodId, setPeriodId] = useState("");
  const [notes, setNotes] = useState("");

  const unpostedQ = useQuery(
    equipmentUnpostedTimeEntriesQueryOptions({
      project_id: projectFilter || undefined,
      equipment_id: equipmentFilter || undefined,
      limit: 500,
    }),
  );
  const entries: EquipmentTimeEntry[] = unpostedQ.data?.items ?? [];

  const equipmentListQ = useQuery(equipmentItemsQueryOptions({ active_only: true }));
  const equipmentList: EquipmentItem[] = equipmentListQ.data ?? [];
  const equipmentByCode = useMemo(() => {
    const m = new Map<string, EquipmentItem>();
    for (const e of equipmentList) m.set(e.id, e);
    return m;
  }, [equipmentList]);

  const periodsQ = useQuery(periodsListQueryOptions());
  const openPeriods = (periodsQ.data ?? []).filter((p) => p.status === "open");

  // Auto-select the period matching posting_date if not yet set.
  useMemo(() => {
    if (!periodId && openPeriods.length > 0 && postingDate) {
      const match = openPeriods.find(
        (p) => p.start_date <= postingDate && postingDate <= p.end_date,
      );
      if (match) setPeriodId(match.id);
    }
  }, [openPeriods, postingDate, periodId]);

  const selectedEntries = entries.filter((e) => selected[e.id]);

  const totals = useMemo(() => {
    let own = 0;
    let op = 0;
    let tr = 0;
    for (const e of selectedEntries) {
      own += Number(e.ownership_amount ?? 0);
      op += Number(e.operating_amount ?? 0);
      tr += Number(e.transport_amount ?? 0);
    }
    return { own, op, tr, total: own + op + tr, count: selectedEntries.length };
  }, [selectedEntries]);

  // Sanity check: do any selected entries reference different clearing
  // accounts? The server will 422 if so, but we surface it on the preview.
  const clearingAccounts = useMemo(() => {
    const ids = new Set<string>();
    for (const e of selectedEntries) {
      const eq = equipmentByCode.get(e.equipment_id);
      if (eq?.gl_clearing_account_id) ids.add(eq.gl_clearing_account_id);
    }
    return ids;
  }, [selectedEntries, equipmentByCode]);

  // Group selected entries for the preview.
  const grouped = useMemo(() => {
    const g = new Map<string, { project_id: string | null; cost_code_id: string | null; total: number }>();
    for (const e of selectedEntries) {
      const own = Number(e.ownership_amount ?? 0);
      const op = Number(e.operating_amount ?? 0);
      const tr = Number(e.transport_amount ?? 0);
      const key = `${e.project_id ?? "_"}|${e.cost_code_id ?? "_"}`;
      const prev = g.get(key);
      const sum = own + op + tr;
      if (prev) prev.total += sum;
      else
        g.set(key, {
          project_id: e.project_id,
          cost_code_id: e.cost_code_id,
          total: sum,
        });
    }
    return Array.from(g.values());
  }, [selectedEntries]);

  const postMut = useMutation({
    mutationFn: async () =>
      api<EquipmentCostPosting>(
        "/api/construction/equipment/cost-postings",
        {
          method: "POST",
          body: {
            time_entry_ids: selectedEntries.map((e) => e.id),
            posting_date: postingDate,
            period_id: periodId,
            notes: notes || null,
          },
        },
      ),
    onSuccess: (posting) => {
      toast.success(
        `Posted ${posting.line_count} entries totaling ${fmtMoney(posting.total_charge_out)}`,
      );
      qc.invalidateQueries({ queryKey: equipmentPostingKeys.all });
      qc.invalidateQueries({ queryKey: ["equipment-items"] });
      navigate({
        to: "/equipment/cost-postings/$postingId",
        params: { postingId: posting.id },
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!packLoading && !enabled) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => navigate({ to: "/equipment" })}
          >
            Back to Equipment
          </Button>
          <Typography variant="h5" fontWeight={600}>
            Equipment cost posting
          </Typography>
        </Stack>
        <HelpHint
          docPath="equipment-costing/posting-to-job-cost.md"
          label="Posting help"
        />
      </Stack>

      <Stepper activeStep={stepIdx} sx={{ mb: 4 }}>
        {STEPS.map((s) => (
          <Step key={s}>
            <StepLabel>{s}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {stepIdx === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} mb={2}>
            <Box sx={{ minWidth: 280 }}>
              <ProjectIdSelect
                value={projectFilter}
                label="Project"
                onChange={setProjectFilter}
                statusFilter={["active"]}
                placeholder="All active projects…"
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Equipment</InputLabel>
              <Select
                value={equipmentFilter}
                label="Equipment"
                onChange={(e) => setEquipmentFilter(e.target.value)}
              >
                <MenuItem value="">All equipment</MenuItem>
                {equipmentList.map((eq) => (
                  <MenuItem key={eq.id} value={eq.id}>
                    {eq.code} — {eq.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {unpostedQ.isLoading && <CircularProgress size={20} />}
          {unpostedQ.error && (
            <Alert severity="error">Failed to load unposted entries.</Alert>
          )}
          {!unpostedQ.isLoading && entries.length === 0 && (
            <Alert severity="info">
              No unposted equipment time entries. New time entries will appear
              here once they're created.
            </Alert>
          )}

          {entries.length > 0 && (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={
                          entries.length > 0 &&
                          entries.every((e) => selected[e.id])
                        }
                        indeterminate={
                          entries.some((e) => selected[e.id]) &&
                          !entries.every((e) => selected[e.id])
                        }
                        onChange={(_, v) => {
                          const next = { ...selected };
                          for (const e of entries) next[e.id] = v;
                          setSelected(next);
                        }}
                      />
                    </TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Equipment</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Run hrs</TableCell>
                    <TableCell align="right">Ownership</TableCell>
                    <TableCell align="right">Operating</TableCell>
                    <TableCell align="right">Transport</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((e) => {
                    const eq = equipmentByCode.get(e.equipment_id);
                    const total =
                      Number(e.ownership_amount ?? 0) +
                      Number(e.operating_amount ?? 0) +
                      Number(e.transport_amount ?? 0);
                    return (
                      <TableRow key={e.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={!!selected[e.id]}
                            onChange={(_, v) =>
                              setSelected((p) => ({ ...p, [e.id]: v }))
                            }
                          />
                        </TableCell>
                        <TableCell>{e.work_date}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {eq ? eq.code : e.equipment_id.slice(0, 8) + "…"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                          {e.project_id
                            ? e.project_id.slice(0, 8) + "…"
                            : "—"}
                        </TableCell>
                        <TableCell align="right">{e.run_hours}</TableCell>
                        <TableCell align="right">
                          {fmtMoney(e.ownership_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtMoney(e.operating_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtMoney(e.transport_amount)}
                        </TableCell>
                        <TableCell align="right">{fmtMoney(total)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="text.secondary">
                  {totals.count} selected · ownership {fmtMoney(totals.own)} ·
                  operating {fmtMoney(totals.op)} · transport{" "}
                  {fmtMoney(totals.tr)} · <b>{fmtMoney(totals.total)}</b>
                </Typography>
                <Button
                  variant="contained"
                  disabled={totals.count < 1}
                  onClick={() => setStepIdx(1)}
                >
                  Next
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      )}

      {stepIdx === 1 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Posting details
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Posting date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={postingDate}
              onChange={(e) => {
                setPostingDate(e.target.value);
                // Re-resolve period to match.
                const p = openPeriods.find(
                  (per) =>
                    per.start_date <= e.target.value &&
                    e.target.value <= per.end_date,
                );
                if (p) setPeriodId(p.id);
              }}
              sx={{ maxWidth: 240 }}
            />
            <FormControl size="small" sx={{ maxWidth: 360 }}>
              <InputLabel>Fiscal period</InputLabel>
              <Select
                value={periodId}
                label="Fiscal period"
                onChange={(e) => setPeriodId(e.target.value)}
              >
                {openPeriods.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    Period {p.period_no} ({p.start_date} – {p.end_date})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes (optional)"
              size="small"
              multiline
              minRows={2}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Alert severity="info">
              You're posting <b>{totals.count}</b> entries totaling{" "}
              <b>{fmtMoney(totals.total)}</b>.
            </Alert>
          </Stack>
          <Stack direction="row" spacing={1} mt={3}>
            <Button onClick={() => setStepIdx(0)}>Back</Button>
            <Button
              variant="contained"
              disabled={!periodId}
              onClick={() => setStepIdx(2)}
            >
              Next: Preview
            </Button>
          </Stack>
        </Paper>
      )}

      {stepIdx === 2 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Journal preview
          </Typography>

          {clearingAccounts.size > 1 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Selected entries reference {clearingAccounts.size} different
              clearing accounts. The server will reject this batch (one
              clearing account per posting). Go back and split the batch by
              equipment.
            </Alert>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Side</TableCell>
                <TableCell>Project / cost code</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map((g, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      DR
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                    {g.project_id
                      ? g.project_id.slice(0, 8) + "…"
                      : "(no project)"}{" "}
                    /{" "}
                    {g.cost_code_id
                      ? g.cost_code_id.slice(0, 8) + "…"
                      : "(no cost code)"}
                  </TableCell>
                  <TableCell align="right">{fmtMoney(g.total)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    CR
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                  Equipment Allocation Clearing
                </TableCell>
                <TableCell align="right">{fmtMoney(totals.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />

          {postMut.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {String(postMut.error)}
            </Alert>
          )}

          <Stack direction="row" spacing={1}>
            <Button onClick={() => setStepIdx(1)}>Back</Button>
            <Button
              variant="contained"
              disabled={postMut.isPending || clearingAccounts.size > 1}
              onClick={() => postMut.mutate()}
            >
              {postMut.isPending
                ? "Posting…"
                : `Post ${totals.count} entries · ${fmtMoney(totals.total)}`}
            </Button>
            <Button
              component={Link}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              to={"/equipment/cost-postings" as any}
            >
              Past postings
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
